import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Grid, Input, Space, Switch, Tooltip, Typography, Upload, notification } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';

import { axiosGet, axiosPost } from '../../services/http.services';
import {
  API_BASE_URL,
  API_CRON_ENABLED,
  API_CRON_STATUS,
  API_DASHBOARD_BACKUP_EXPORT,
  API_DASHBOARD_BACKUP_IMPORT,
  API_FULL_SYSTEM_BACKUP_EXPORT,
  API_FULL_SYSTEM_BACKUP_IMPORT,
  API_GET_DATA,
  API_START_CRON,
  API_UPDATE_API_KEYS,
} from '../../api/api';


const { Title, Text } = Typography;
const { useBreakpoint } = Grid;


const panelStyle = {
  padding: 25,
  width: '100%',
  border: '1px solid #ddd',
  borderRadius: '20px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#fff',
  height: '100%',
};


const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
};


const downloadJson = (payload, fileName) => {
  downloadBlob(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), fileName);
};


const resolveDownloadFileName = (contentDisposition, fallbackFileName) => {
  const utfMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const basicMatch = contentDisposition?.match(/filename="?([^"]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallbackFileName;
};


const formatUpdatedTimestamp = (value) => {
  if (!value) {
    return 'Not available yet';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Not available yet';
  }

  return parsedDate?.toDateString().replace(' ', ', ') + ' ' + parsedDate?.toLocaleTimeString();
};


const AdminControls = () => {
  const screens = useBreakpoint();
  const [tenantID, setTenantID] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [backupFile, setBackupFile] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [systemBackupFile, setSystemBackupFile] = useState(null);
  const [systemBackupBusy, setSystemBackupBusy] = useState(false);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronEnabled, setCronEnabled] = useState(true);
  const [cronSaving, setCronSaving] = useState(false);
  const [lastUpdatedTime, setLastUpdatedTime] = useState('Not available yet');

  const openNotification = (type, message, description) => {
    notification[type]({
      message,
      description,
      placement: 'bottomRight',
    });
  };

  const handleDownload = (fileName) => {
    const link = `${API_BASE_URL}/download/${fileName}`;
    window.open(link, '_blank');
  };

  const loadLastUpdatedTime = useCallback(async () => {
    try {
      const response = await axiosGet(API_GET_DATA);
      setLastUpdatedTime(formatUpdatedTimestamp(response?.data?.LastUpdatedTime));
    } catch (error) {
      setLastUpdatedTime('Not available yet');
    }
  }, []);

  const loadCronState = useCallback(async () => {
    try {
      const response = await axiosGet(API_CRON_STATUS);
      setCronEnabled(response?.data?.enabled !== false);
    } catch (error) {
      setCronEnabled(true);
    }
  }, []);

  useEffect(() => {
    loadLastUpdatedTime();
    loadCronState();
  }, [loadCronState, loadLastUpdatedTime]);

  const triggerCron = async () => {
    try {
      setCronLoading(true);
      await axiosGet(API_START_CRON);
      openNotification(
        'success',
        'Cron Triggered',
        'The cron trigger request has been sent. Usage records will be hard reset from the downloaded workbook.'
      );
      setTimeout(() => {
        loadLastUpdatedTime();
        loadCronState();
      }, 3000);
    } catch (error) {
      openNotification(
        'error',
        'Cron Failed',
        error?.response?.data?.message || 'The cron trigger could not be started.'
      );
    } finally {
      setCronLoading(false);
    }
  };

  const toggleCron = async (checked) => {
    try {
      setCronSaving(true);
      await axiosPost(API_CRON_ENABLED, { enabled: checked });
      setCronEnabled(checked);
      openNotification('success', checked ? 'Cron Enabled' : 'Cron Disabled', checked ? 'Scheduled cron is active.' : 'Scheduled cron is paused.');
    } catch (error) {
      openNotification(
        'error',
        'Cron Setting Failed',
        error?.response?.data?.message || 'Cron setting could not be updated.'
      );
      loadCronState();
    } finally {
      setCronSaving(false);
    }
  };

  const updateKeys = async () => {
    if (tenantID.length < 30 || clientId.length < 30 || clientSecret.length < 30) {
      openNotification('error', 'Error', 'Please fill all the API key fields.');
      return;
    }

    try {
      await axiosPost(API_UPDATE_API_KEYS, { tenantID, clientId, clientSecret });
      openNotification('success', 'Keys Updated', 'The API keys were updated successfully.');
    } catch (error) {
      openNotification('error', 'Error', 'API keys could not be updated.');
    }
  };

  const exportBackup = async () => {
    try {
      setBackupBusy(true);
      const response = await axiosGet(API_DASHBOARD_BACKUP_EXPORT);
      const exportedAt = response?.data?.exported_at?.replace(/[:.]/g, '-') || 'backup';
      downloadJson(response.data, `dashboard-config-backup-${exportedAt}.json`);
      openNotification('success', 'Backup Exported', 'Dashboard config backup JSON downloaded.');
    } catch (error) {
      openNotification('error', 'Export Failed', 'Dashboard config backup could not be exported.');
    } finally {
      setBackupBusy(false);
    }
  };

  const importBackup = async () => {
    if (!backupFile) {
      openNotification('error', 'No File Selected', 'Choose a dashboard config backup JSON file first.');
      return;
    }

    try {
      setBackupBusy(true);
      const text = await backupFile.text();
      await axiosPost(API_DASHBOARD_BACKUP_IMPORT, JSON.parse(text));
      openNotification('success', 'Backup Imported', 'Dashboard config backup imported successfully.');
      setBackupFile(null);
    } catch (error) {
      openNotification(
        'error',
        'Import Failed',
        error?.response?.data?.message || 'Dashboard config backup could not be imported.'
      );
    } finally {
      setBackupBusy(false);
    }
  };

  const exportFullSystemBackup = async () => {
    try {
      setSystemBackupBusy(true);
      const response = await axiosGet(API_FULL_SYSTEM_BACKUP_EXPORT, { responseType: 'blob' });
      const fileName = resolveDownloadFileName(
        response?.headers?.['content-disposition'],
        `full-system-backup-${Date.now()}.sql`
      );
      downloadBlob(response.data, fileName);
      openNotification('success', 'SQL Backup Exported', 'Full system SQL dump downloaded.');
    } catch (error) {
      openNotification(
        'error',
        'SQL Export Failed',
        error?.response?.data?.message || 'Full system SQL backup could not be exported.'
      );
    } finally {
      setSystemBackupBusy(false);
    }
  };

  const importFullSystemBackup = async () => {
    if (!systemBackupFile) {
      openNotification('error', 'No File Selected', 'Choose a full system SQL backup file first.');
      return;
    }

    try {
      setSystemBackupBusy(true);
      const formData = new FormData();
      formData.append('file', systemBackupFile);
      await axiosPost(API_FULL_SYSTEM_BACKUP_IMPORT, formData);
      openNotification('success', 'SQL Backup Imported', 'Full system SQL backup imported successfully.');
      setSystemBackupFile(null);
    } catch (error) {
      openNotification(
        'error',
        'SQL Import Failed',
        error?.response?.data?.message || 'Full system SQL backup could not be imported.'
      );
    } finally {
      setSystemBackupBusy(false);
    }
  };

  const backupDescription = useMemo(
    () =>
      'Snapshot includes airlines, airports, app downloads, users, and settings. Without Usage records.',
    []
  );

  const fullSystemBackupDescription = useMemo(
    () =>
      'Exports the full MySQL schema and data as a SQL dump, including usage records. Import will replace the current database contents.',
    []
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: screens.lg ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        gap: 20,
        alignItems: 'start',
      }}
    >
      <div style={panelStyle}>
        <Title level={3}>Download Reports</Title>

        <Space direction="horizontal" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text type="primary">The Report contains the raw airport entry workbook.</Text>
            <Text type="primary">The Consolidated Report contains the processed usage workbook.</Text>
          </Space>

          <Divider type="vertical" style={{ height: 90 }} />

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button type="primary" size="large" onClick={() => handleDownload('report')} block style={{ borderRadius: '5px 25px' }}>
              Download AP-Report
            </Button>

            <Button type="primary" size="large" onClick={() => handleDownload('consolidated')} block style={{ borderRadius: '5px 25px' }}>
              Download Consolidated
            </Button>
          </Space>
        </Space>
      </div>

      <div style={panelStyle}>
        <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'center', marginBottom: 10 }}>
          <Title level={3} style={{ margin: 0 }}>
            Jobs
          </Title>
          <Text type="secondary" style={{ marginLeft: 30 }}>
            Current Schedule (12 AM IST, 12 PM IST)
          </Text>
        </Space>

		<div style={{ marginBottom: 10, marginTop: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Switch checked={cronEnabled} loading={cronSaving} onChange={toggleCron} />
              <Text type="secondary">{cronEnabled ? 'Cron active' : 'Cron paused'}</Text>
        </div>

        <Space direction="horizontal" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="primary">
              Trigger the scheduled cron to download reports and hard reset usage records from the workbook.
            </Text>
         
            <div style={{ marginTop: 15 }}>
              <Text type="secondary">Updated: {lastUpdatedTime}</Text>
            </div>
          </div>

          <Button
            type="primary"
            size="large"
            onClick={triggerCron}
            block
            style={{ borderRadius: '5px 25px', paddingInline: 50, marginLeft: 15, top: -20 }}
            disabled={cronLoading || cronSaving || !cronEnabled}
            loading={cronLoading}
          >
            Trigger Cron
          </Button>
        </Space>
      </div>

      <div style={panelStyle}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Dashboard Config Backup
          </Title>
          <Text type="secondary">{backupDescription}</Text>

            <Button type="primary" icon={<DownloadOutlined />} loading={backupBusy} onClick={exportBackup}>
              Export JSON Backup
            </Button>
		  <Space direction="horizontal" size="middle" style={{ width: '100%', flexWrap: 'wrap', marginTop: 15, marginBottom: 15 }}>
            <Upload
              beforeUpload={(file) => {
                setBackupFile(file);
                return false;
              }}
              maxCount={1}
              accept=".json"
              showUploadList={{ showRemoveIcon: true }}
              onRemove={() => {
                setBackupFile(null);
              }}
            >
              <Button icon={<UploadOutlined />}>Choose Backup File</Button>
            </Upload>

            <Button onClick={importBackup} loading={backupBusy}>
              Import Backup
            </Button>
          </Space>
        </Space>
      </div>

      <div style={{ ...panelStyle, position: 'relative' }}>
        <div
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 999 }}
          onClick={() => window.open('https://www.youtube.com/watch?v=GmWejAg2cfA', '_blank')?.focus()}
        >
          <Tooltip title="Click here to open video on how to create API keys from AZURE.">
            <span
              style={{
                fontSize: 14,
                fontWeight: 'bold',
                fontStyle: 'italic',
                color: '#1890ff',
                borderRadius: 20,
                borderWidth: 2,
                padding: 5,
                paddingInline: 13,
                cursor: 'default',
              }}
            >
              i
            </span>
          </Tooltip>
        </div>

        <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'center', marginBottom: 10 }}>
          <Title level={3} style={{ margin: 0 }}>
            API Keys
          </Title>
          <Text type="secondary" style={{ marginLeft: 15 }}>
            Update with caution
          </Text>
        </Space>

        <Space direction="horizontal" size="large" style={{ width: '100%', margin: 10 }}>
          <div style={{ width: 100 }}>
            <Text type="primary">Tenant Id:</Text>
          </div>
          <Input style={{ width: 500 }} value={tenantID} onChange={(event) => setTenantID(event.target.value)} placeholder="Enter Tenant Id" />
        </Space>

        <Space direction="horizontal" size="large" style={{ width: '100%', margin: 10 }}>
          <div style={{ width: 100 }}>
            <Text type="primary">Client Id:</Text>
          </div>
          <Input style={{ width: 500 }} value={clientId} onChange={(event) => setClientId(event.target.value)} placeholder="Enter Client Id" />
        </Space>

        <Space direction="horizontal" size="large" style={{ width: '100%', margin: 10 }}>
          <div style={{ width: 100 }}>
            <Text type="primary">Client Secret:</Text>
          </div>
          <Input
            style={{ width: 500 }}
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            placeholder="Enter Client Secret"
          />
        </Space>

        <Button
          type="primary"
          size="large"
          onClick={updateKeys}
          block
          style={{ borderRadius: '5px 25px', paddingInline: 50, marginLeft: 10, width: 300, marginTop: 15 }}
        >
          Update Keys
        </Button>
      </div>

      <div style={panelStyle}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Full System SQL Backup
          </Title>
          <Text type="secondary">{fullSystemBackupDescription}</Text>

          <Button type="primary" icon={<DownloadOutlined />} loading={systemBackupBusy} onClick={exportFullSystemBackup}>
            Export SQL Dump
          </Button>

          <Space direction="horizontal" size="middle" style={{ width: '100%', flexWrap: 'wrap', marginTop: 15, marginBottom: 15 }}>
            <Upload
              beforeUpload={(file) => {
                setSystemBackupFile(file);
                return false;
              }}
              maxCount={1}
              accept=".sql"
              showUploadList={{ showRemoveIcon: true }}
              onRemove={() => {
                setSystemBackupFile(null);
              }}
            >
              <Button icon={<UploadOutlined />}>Choose SQL File</Button>
            </Upload>

            <Button onClick={importFullSystemBackup} loading={systemBackupBusy}>
              Import SQL Dump
            </Button>
          </Space>
        </Space>
      </div>
    </div>
  );
};


export default AdminControls;
